/**
 * Configuración del auth-client compartido.
 *
 * Cada app de la familia (speak, spar, …) llama `configureAuthClient(...)` UNA
 * vez en su entry (`main.tsx`), antes del primer render, para declarar:
 *
 *  - `appSource`: el identificador de la app para el audit log de auth del
 *    backend (header `X-Orborix-App` → `auth_events.app_source`; el backend
 *    valida contra un allow-list en `apps/backend/src/lib/auth-events.ts`).
 *  - `signupNativeLanguage`: la lengua nativa con la que la app CREA cuentas
 *    (OTP / Google); el backend rechaza un alta sin ella.
 *  - `native` (opcional): el puente al camino nativo Capacitor (Fase 23 de
 *    speak). Las apps web-only lo omiten y todo el camino nativo desaparece:
 *    rutas relativas same-origin + cookie httpOnly, cero tocar de tokens.
 *
 * Fail-fast (regla del repo): usar `apiFetch`/`AuthProvider` sin configurar
 * lanza con un mensaje claro — no hay un `appSource` default silencioso que
 * dejaría el audit log mintiendo.
 */

/**
 * Puente nativo (Capacitor). La implementación vive en la app (ej.
 * `apps/speak/src/lib/{platform,nativeAuth}.ts`) — el package no depende de
 * `@capacitor/*`. Todas las funciones deben ser no-op cuando `isNative` es
 * `false` (así una app híbrida usa el mismo bridge en web y nativo).
 */
export interface NativeAuthBridge {
  /** `true` sólo dentro del wrapper nativo (iOS/Android). */
  isNative: boolean;
  /** Base absoluta del backend para requests nativos (fail-fast si falta). */
  apiBaseUrl: () => string;
  /** Boot: hidrata el Bearer token del storage durable → `setAuthToken`. */
  loadToken: () => Promise<void>;
  /** Post-login nativo: persiste el token que el backend devolvió en el body. */
  persistToken: (token: string) => Promise<void>;
  /** Signout: borra el token (in-memory + storage durable). */
  clearToken: () => Promise<void>;
}

export interface AuthClientConfig {
  /** Identidad de la app para el audit de auth (`X-Orborix-App`). */
  appSource: string;
  /**
   * Native language (ISO 639-1/3, `'es'`) every account this app CREATES is
   * born with — the backend rejects a sign-up without it (owner decision
   * 2026-10-09: the native language is mandatory; the memory distiller writes
   * each fact in it). Each app declares its own in `main.tsx`.
   */
  signupNativeLanguage: string;
  /** Puente Capacitor; ausente = app web-only (camino nativo inexistente). */
  native?: NativeAuthBridge;
}

let config: AuthClientConfig | null = null;

/** Configura el auth-client. Llamar UNA vez en el entry de la app. */
export function configureAuthClient(next: AuthClientConfig): void {
  config = next;
}

/** Config actual. Lanza si la app no llamó `configureAuthClient` (fail-fast). */
export function getAuthClientConfig(): AuthClientConfig {
  if (!config) {
    throw new Error(
      'auth-client sin configurar: llamá configureAuthClient({ appSource, signupNativeLanguage }) en el ' +
        'entry de la app (main.tsx) antes de usar apiFetch/authApi/AuthProvider.',
    );
  }
  return config;
}

/**
 * Config actual o `null` si la app no configuró. Sólo para `apiFetch`: el
 * bridge nativo es OPCIONAL por diseño (sin config ⇒ camino web same-origin),
 * y una app nativa mal configurada igual falla ruidoso en el boot — el primer
 * `me()` del AuthProvider pasa por `getAuthClientConfig()` (que sí lanza).
 */
export function getAuthClientConfigOrNull(): AuthClientConfig | null {
  return config;
}
